import React, { createContext, useCallback, useContext, useRef, useState, ReactNode } from 'react';
import { CheckCircle, WarningCircle, X } from 'phosphor-react';

type Tipo = 'sucesso' | 'erro';

interface Toast {
  id: number;
  tipo: Tipo;
  texto: string;
  /** Marcado no fim da vida: é o que dispara a animação de saída antes de sumir. */
  saindo?: boolean;
}

interface ToastContextType {
  /** Ação deu certo. */
  sucesso: (texto: string) => void;
  /** Ação falhou — o texto costuma vir do getApiErrorMessage. */
  erro: (texto: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

/** Quanto tempo o aviso fica na tela. Mesma janela do antigo notify() de Configurações. */
const DURACAO_MS = 4000;
/** Casa com o animate-exit do tailwind.config.js. */
const SAIDA_MS = 200;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const proximoId = useRef(0);

  // Fechar é sempre em dois tempos: marca `saindo` (a CSS anima) e só depois
  // tira da lista. Vale tanto para o prazo quanto para o X.
  const fechar = useCallback((id: number) => {
    setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, saindo: true } : t)));
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), SAIDA_MS);
  }, []);

  const mostrar = (tipo: Tipo, texto: string) => {
    const id = proximoId.current++;
    setToasts((prev) => [...prev, { id, tipo, texto }]);
    setTimeout(() => fechar(id), DURACAO_MS - SAIDA_MS);
  };

  return (
    <ToastContext.Provider value={{
      sucesso: (texto) => mostrar('sucesso', texto),
      erro: (texto) => mostrar('erro', texto),
    }}>
      {children}
      <ToastHost toasts={toasts} onFechar={fechar} />
    </ToastContext.Provider>
  );
}

/**
 * A pilha de avisos. Fica no topo porque o rodapé é da BottomNav e das barras fixas de
 * "Salvar" — aviso escondido atrás de botão não é aviso.
 *
 * Some sozinho em 4s, e tem X: aviso temporizado precisa de uma forma de dispensar
 * (WCAG 2.2.1). O container continua `pointer-events-none` para não roubar o toque da
 * tela por baixo; só o botão volta a receber evento.
 */
function ToastHost({ toasts, onFechar }: { toasts: Toast[]; onFechar: (id: number) => void }) {
  if (toasts.length === 0) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed top-4 left-0 right-0 z-[60] px-5 pointer-events-none flex flex-col gap-2 items-center"
    >
      {toasts.map(({ id, tipo, texto, saindo }) => (
        <div
          key={id}
          data-state={saindo ? 'closed' : 'open'}
          className={`
            w-full max-w-lg flex items-center gap-3 px-4 py-3 rounded-2xl shadow-soft-md
            border data-[state=open]:animate-slide-down data-[state=closed]:animate-exit
            ${tipo === 'sucesso'
              ? 'bg-sage-50 dark:bg-sage-900/30 border-sage-200 dark:border-sage-800 text-sage-700 dark:text-sage-300'
              : 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300'
            }
          `}
        >
          {tipo === 'sucesso'
            ? <CheckCircle className="w-5 h-5 flex-shrink-0" weight="fill" />
            : <WarningCircle className="w-5 h-5 flex-shrink-0" weight="fill" />}
          <p className="flex-1 text-sm font-medium">{texto}</p>
          <button
            onClick={() => onFechar(id)}
            aria-label="Dispensar aviso"
            className="pointer-events-auto -mr-1 p-1 rounded-lg opacity-60 hover:opacity-100 transition-opacity"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  );
}

export function useToast(): ToastContextType {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast precisa estar dentro do ToastProvider');
  }
  return context;
}
